import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { logWarn } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const QUICK = ["On my way", "Arrived at pickup", "Running 10 min late", "Gate code?", "Delivered — left at front desk"];

export default function GigChat({ job, open, onOpenChange, onRead }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const closed = ["completed", "cancelled"].includes(job.status);

  const onReadRef = useRef(onRead); onReadRef.current = onRead;
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/gigs/jobs/${job.job_id}/messages`); setMessages(data); onReadRef.current?.(); }
    catch (e) { logWarn("gig chat poll failed", e?.response?.status || e?.message); }
  }, [job.job_id]);
  useEffect(() => { if (!open) return; load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [open, load]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const t = (text ?? input).trim(); if (!t || busy) return;
    setBusy(true);
    try { const { data } = await api.post(`/gigs/jobs/${job.job_id}/messages`, { text: t }); setMessages((m) => [...m, data]); setInput(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0B0D12] border-slate-800 w-full sm:max-w-md flex flex-col p-0" data-testid="gig-chat-drawer">
        <SheetHeader className="px-5 py-4 border-b border-slate-800">
          <SheetTitle className="text-white font-display flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-400" />{job.title}</SheetTitle>
          <p className="text-xs text-slate-500">{job.is_poster ? `Driver: ${job.driver_name}` : `Business: ${job.poster_name}`} · {job.status.replace("_", " ")}</p>
        </SheetHeader>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="gig-chat-messages">
          {messages.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No messages yet. Share gate codes, timing, or where to leave the items.</p>}
          {messages.map((m) => { const mine = m.sender_id === user?.user_id; return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-blue-500 text-white rounded-tr-sm" : "bg-slate-800/80 text-slate-100 rounded-tl-sm"}`}>
                {!mine && <p className="text-[10px] font-mono uppercase text-slate-400 mb-0.5">{m.sender_name} · {m.role}</p>}
                <p className="whitespace-pre-wrap">{m.text}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-blue-100/70" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
              </div>
            </div>
          ); })}
        </div>
        <div className="p-4 border-t border-slate-800 space-y-2">
          {closed ? <p className="text-xs text-slate-500 text-center">This run is closed — chat is read-only.</p> : <>
            <div className="flex gap-1.5 flex-wrap">{QUICK.map((q) => <button key={q} onClick={() => send(q)} disabled={busy} data-testid="gig-chat-quick" className="px-2 py-0.5 rounded-full text-[11px] border border-slate-700 text-slate-300 hover:border-cyan-500/50">{q}</button>)}</div>
            <div className="flex gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message…" data-testid="gig-chat-input" className="bg-slate-900 border-slate-800" />
              <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="gig-chat-send" className="bg-blue-500 hover:bg-blue-600 shrink-0">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
            </div>
          </>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
